/**
 * ファイル名の拡張子から、そのファイルらしい印を出す。
 *
 * タブが index.html と style.css で並ぶとき、文字だけだと
 * 今どっちを触っているかが一目で分からない。色と形が付くと迷わない。
 * 知らない拡張子は素のファイルの印に落とす
 */
import { FileIcon } from "../../base/Icon";
import { LanguageIcon, hasLanguageIcon } from "../../base/Icon/LanguageIcon";

export interface FileTypeIconProps {
  /** index.html のようなファイル名 */
  name: string;
  className?: string;
}

export function FileTypeIcon({ name, className = "w-4 h-4 shrink-0" }: FileTypeIconProps) {
  const ext = name.split(".").pop()?.toLowerCase();

  if (ext === "html" || ext === "htm") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path fill="#E44D26" d="M1.5 0h21l-1.91 21.56L11.98 24l-8.57-2.44L1.5 0z" />
        <path fill="#F16529" d="M12 2.5v19.35l6.92-1.97L20.46 2.5H12z" />
        <path
          fill="#EBEBEB"
          d="M12 9.5H8.5l-.24-2.7H12V4.16H5.55l.06.7.63 7.09H12V9.5zm0 6.99l-2.9-.78-.19-2.08H6.3l.36 4.09 5.34 1.48v-2.71z"
        />
        <path
          fill="#fff"
          d="M11.99 9.5v2.45h3.24l-.31 3.41-2.93.79v2.71l5.35-1.48.04-.44.61-6.87.06-.7h-6.06zm0-5.34V6.8h6.22l.05-.58.12-1.36.06-.7h-6.45z"
        />
      </svg>
    );
  }

  if (ext === "css") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path fill="#1572B6" d="M1.5 0h21l-1.91 21.56L11.98 24l-8.57-2.44L1.5 0z" />
        <path fill="#33A9DC" d="M12 2.5v19.35l6.92-1.97L20.46 2.5H12z" />
        <path fill="#fff" d="M12 9.76h3.47l.24-2.68H12V4.66h6.44l-.06.69-.63 7.06H12V9.76z" />
        <path
          fill="#EBEBEB"
          d="M12.01 16l-2.9-.79-.19-2.08H6.44l.37 4.09 5.2 1.44V16zm3.17-4.21l-.31 3.5-2.91.79v2.66l5.2-1.44.04-.43.45-4.9.06-.7h-2.53z"
        />
        <path fill="#EBEBEB" d="M12.01 4.66v2.42H5.8l-.05-.58-.12-1.15-.06-.69h6.44z" />
        <path fill="#fff" d="M12 9.76v2.42H9.17l-.05-.58-.11-1.15-.06-.69H12z" />
      </svg>
    );
  }

  if (ext === "js" || ext === "mjs" || ext === "cjs") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path fill="#F7DF1E" d="M0 0h24v24H0V0z" />
        <path
          fill="#000"
          d="M6.5 20.05l1.84-1.11c.35.63.68 1.16 1.46 1.16.75 0 1.22-.29 1.22-1.43v-7.75h2.25v7.78c0 2.33-1.37 3.39-3.36 3.39-1.8 0-2.85-.93-3.38-2.05m7.96-.24l1.83-1.06c.48.79 1.11 1.37 2.22 1.37.94 0 1.53-.47 1.53-1.11 0-.77-.61-1.05-1.64-1.5l-.56-.24c-1.63-.7-2.71-1.57-2.71-3.41 0-1.7 1.29-2.99 3.31-2.99 1.44 0 2.47.5 3.21 1.81l-1.76 1.13c-.39-.7-.81-.97-1.46-.97-.66 0-1.08.42-1.08.97 0 .68.42.95 1.39 1.37l.56.24c1.92.82 3 1.66 3 3.55 0 2.04-1.6 3.16-3.75 3.16-2.1 0-3.46-1-4.13-2.32"
        />
      </svg>
    );
  }

  // jsx と tsx は React のコンポーネントを書くファイルなので、React の印を出す。
  // ここが無いと App.jsx だけ素っ気ない印になり、隣の styles.css より格下に見えてしまう
  if (ext === "jsx" || ext === "tsx") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <circle cx="12" cy="12" r="2.05" fill="#61DAFB" />
        <g fill="none" stroke="#61DAFB" strokeWidth="1">
          <ellipse cx="12" cy="12" rx="11" ry="4.2" />
          <ellipse cx="12" cy="12" rx="11" ry="4.2" transform="rotate(60 12 12)" />
          <ellipse cx="12" cy="12" rx="11" ry="4.2" transform="rotate(120 12 12)" />
        </g>
      </svg>
    );
  }

  if (ext === "ts" || ext === "mts" || ext === "cts") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path fill="#3178C6" d="M0 0h24v24H0V0z" />
        <path
          fill="#fff"
          d="M13.03 12.9v-1.78h-7.2v1.78h2.5v7.1h2.2v-7.1h2.5zm.9 6.72c.36.7 1.03 1.24 1.9 1.55.62.22 1.32.32 2.05.32 2.15 0 3.62-1.12 3.62-3.16 0-1.89-1.08-2.73-3-3.55l-.56-.24c-.97-.42-1.39-.69-1.39-1.37 0-.55.42-.97 1.08-.97.65 0 1.07.27 1.46.97l1.76-1.13c-.74-1.31-1.77-1.81-3.21-1.81-2.02 0-3.31 1.29-3.31 2.99 0 1.84 1.08 2.71 2.71 3.41l.56.24c1.03.45 1.64.73 1.64 1.5 0 .64-.59 1.11-1.53 1.11-1.11 0-1.74-.58-2.22-1.37l-1.83 1.06z"
        />
      </svg>
    );
  }

  /*
  LANGUAGE_ICONS を直に引かない。**あの表は1色版で、Python が真っ青になる。**
  main.py のタブに出ていたのは simple-icons の単色版で、本物のロゴにある
  黄色い側が落ちていた。LanguageIcon は py を python に寄せたうえで、
  手描きの2色ロゴを持っている言語はそちらを返す。
  持っていない言語は同じ1色版に落ちるので、他の拡張子の見た目は変わらない。
  */
  if (ext && hasLanguageIcon(ext)) {
    return <LanguageIcon language={ext} className={className} />;
  }

  return <FileIcon className={`${className} text-main`} />;
}

